import React, { useCallback } from 'react';
import fr from 'facet-rest';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import DialogActions from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/DialogActions';
import { useAnnouncementContext } from 'Context/AnnouncementContext';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import SimplePicker from 'Helpers/SimplePicker';
import {
  FDBackButton,
  FDHeadlineText,
  FDSecondaryActionIconButton,
  FDSecondaryActionButton,
  FDStack,
  FDPrimaryActionButton,
  FDDisplayText, FDImage, FDBox, FDDialogContent, FDBodyText,
} from '@facet-wealth/design';
import { Box } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import useMobileLayout from 'Hooks/useMobileLayout';
import classNames from 'classnames';
import { secureFilesFolderUri } from 'Consts/clientUris';
import { useHistory } from 'react-router-dom';
import { LottieRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/LottieRel';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { LottieLoadingLayout } from 'Organisms/LottieControl/LottieLoadingLayout';
import { dataAttrs, testAttr } from 'Helpers/Testing';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { navigateToPage } from 'Views/Page/Content/Templates';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import Progress from './Progress';

const metaOverrides = buildFacetDesignOverride()
  .nameOverride('headline', {
    _component: FDDisplayText,
    _customProps: {
      align: 'center',
    },
  }, overrideReadOnly)
  .nameOverride('title', {
    _component: () => Richtext,
    _customProps: {
      Component: FDHeadlineText,
      size: 'sm',
      align: 'center',
      color: 'textSecondary',
    },
  }, overrideReadOnly)
  .nameOverride('content', {
    _component: () => Richtext,
    _customProps: {
      unspaced: true,
      Component: FDBodyText,
    },
  }, overrideReadOnly);

const useStyles = buildUseStyles('DialogContent', ((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: theme.dimensions.xl,
    padding: theme.spacing(0),
    gap: `${theme.spacing(4)}px`,
  },
  closeButtonContainer: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    borderRadius: theme.radius.lg,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  },
  bottom: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: `${theme.spacing(2)}px`,
  },
  bottomMinHeight: {
    minHeight: theme.dimensions.xs,
  },
  title: {
    maxWidth: theme.dimensions.md,
  },
  richTextContent: {
    maxWidth: theme.dimensions.lg,
    color: theme.palette.facet.secondaryText,
    textAlign: 'center',
  },
  action: {
    display: 'flex',
    justifyContent: 'center',
  },
})));


const DialogContentWrapper = () => {
  const isMobile = useMobileLayout();
  const classes = useStyles({ isMobile });
  const theme = useTheme();
  const history = useHistory();
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    modal: {
      currentStepResource,
      announcementCollection,
      announcementResource,
      markAsSeen,
      goToPreviousStep,
      goToNextStep,
      clearAnnouncement,
      markAsDismissed,
      isFirstStep,
      isLastStep,
      currentStepIdx,
      totalSteps,
      isDismissible,
    },
  } = useAnnouncementContext();
  const { loadModalCollectionByLink } = useModalContext();

  const markAsCompleted = useCallback(async () => {
    // mark completed
    announcementResource.link('self').copy()
      .withQueryParams().equal('action', 'completed')
      .put();

    // get the redirect
    const redirectRel = announcementResource.withRelationship(
      fr.relationships.redirect.name,
      (rel) => rel,
      () => null,
    );

    if (!redirectRel) {
      clearAnnouncement();
      return;
    }

    if (redirectRel.meta('modal') === true) {
      await loadModalCollectionByLink(redirectRel.link('related').url());
      clearAnnouncement();
      return;
    }

    const resp = await redirectRel.getRelated();

    const [redirectResource] = resp;

    navigateToPage(redirectResource.link('self'), redirectResource.attr('isAgent'));

    clearAnnouncement();
  }, [announcementResource, clearAnnouncement]);

  useComponentDidMount(() => {
    markAsSeen();
  });

  const handleOnClick = async () => {
    setIsLoading(true);
    if (isLastStep) {
      await markAsCompleted();
    } else {
      await goToNextStep();
    }
    setIsLoading(false);
  };

  return (
    <LottieLoadingLayout>
      <FDBox margin={isMobile ? 0 : 3}>
        <FDDialogContent {...dataAttrs('announcement-dialog-content', currentStepResource, { [testAttr.dataIndex]: currentStepIdx.toString() })}>
          <FDStack direction={'column'} alignItems={'center'} spacing={isMobile ? 2 : 4} maxWidth={theme.dimensions.xl}>
            <FDStack padding={0} direction={'column'} alignItems={'center'} justifyContent={'center'} spacing={2} width={'100%'}>
              {currentStepResource.withRelationship(fr.relationships.image.name, (relationship) => {
                const aspectRatio = relationship.meta('aspectRatio');
                const altText = relationship.meta('altText') ?? ''; // If alt text is missing, FD will blow up - use an empty string as in that case
                const url = relationship.link('related').url();
                return (
                  <FDImage alt={altText} src={url} width={'100%'} aspectRatio={aspectRatio} objectFit={'fill'} />
                );
              }, () => null)}
              <LottieRel relationship={currentStepResource.relationship(fr.relationships.lottie.name)} />
              <Progress currentStepIdx={currentStepIdx} totalSteps={totalSteps} />
              {/* TODO DVH is this button getting covered up? */}
              {isDismissible // leave this as the last item in the top section so that the close button will not be covered up by the Lottie
                ? <Box className={classes.closeButtonContainer}><FDSecondaryActionIconButton icon={'close'} onClick={() => markAsDismissed()} /></Box>
                : null}
            </FDStack>
            <Box className={classNames(classes.bottom, { [classes.bottomMinHeight]: totalSteps > 1 })}>
              <FDStack maxWidth={theme.dimensions.lg} spacing={2} direction={'column'}>
                <MetaControl attrName={'headline'} resource={currentStepResource} metaOverrideSet={metaOverrides} />
                <MetaControl attrName={'title'} resource={currentStepResource} metaOverrideSet={metaOverrides} />
              </FDStack>
              <MetaControl attrName={'content'} resource={currentStepResource} metaOverrideSet={metaOverrides} />
            </Box>
          </FDStack>
        </FDDialogContent>
        <DialogActions
          className={classes.action}
        >
          {isFirstStep ? null : (
            <FDBackButton onClick={goToPreviousStep} {...dataAttrs('announcement-back-button')} />
          )}
          <FDStack direction={isMobile ? 'column' : 'row'} spacing={2}>
            <FDPrimaryActionButton
              label={currentStepResource.attr('continueText')}
              loading={isLoading}
              onClick={handleOnClick}
              {...dataAttrs('announcement-continue-button')}
            />
            <SimplePicker
              resource={announcementCollection?.resources()?.head()}
              relationship={fr.relationships.secondaryActionEstatePlanning.name}
              hasRelationship={() => (
                <FDSecondaryActionButton
                  onClick={async () => {
                    history.push(secureFilesFolderUri('Estate Plans'));
                    await markAsDismissed();
                  }}
                  label={'I have an estate plan'}
                />
              )}
            />
          </FDStack>
        </DialogActions>
      </FDBox>
    </LottieLoadingLayout>
  );
};

export default DialogContentWrapper;
